import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";
import { VirtualTableProps } from "./interfaces";
import cn from "classnames";

const VirtualTable = <T extends object>({
    renderRow,
    rowHeight,
    rows,
    header,
    overscan = 10,
    className,
    ...rest
}: VirtualTableProps<T>) => {
    const offset = useSignal(0);
    const height = useSignal(0);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = ref.current;

        if (element) {
            height.value = element.offsetHeight;
        }
    }, [height]);

    const pStart = Math.floor(offset.value / rowHeight);
    const start = overscan ? Math.max(0, pStart - (pStart % overscan)) : pStart;
    const visibleCount = Math.floor(height.value / rowHeight) + overscan;
    const end = start + visibleCount + 1;
    const containerHeight = rows.length * rowHeight;
    const contentTop = start * rowHeight;
    const selection = rows.slice(start, end);

    return (
        <div
            ref={ref}
            role="grid"
            className={cn("w-full", className)}
            onScroll={(event) => {
                offset.value = event.currentTarget.scrollTop;
            }}
            {...rest}
        >
            {header}
            <div
                style={{ height: containerHeight }}
                className="relative min-h-full w-full overflow-hidden"
            >
                <div
                    style={{ top: contentTop }}
                    className="absolute left-0 top-0 h-full w-full overflow-visible"
                >
                    {selection.map((item, idx) =>
                        renderRow(item, {
                            role: "row",
                            "aria-rowindex": start + idx + 1,
                        }),
                    )}
                </div>
            </div>
        </div>
    );
};

export default VirtualTable;
